/**
 * 互动故事相关API接口集合
 * 功能：封装所有与互动故事相关的API调用
 * 
 * 注意：
 * - 视频生成接口（init/continue）需要较长等待时间（30-120秒）
 * - 建议使用轮询状态接口来检查生成进度，而不是等待单次请求完成
 */

import api from './api'; // 导入共享的axios实例

/**
 * 简化的重试机制
 * 与 api.js 中的 retryRequest 保持一致
 */
const retryRequest = async (fn, retries = 2) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return retryRequest(fn, retries - 1);
    }
    throw error;
  }
};

/**
 * 互动故事API服务
 */
export const storyAPI = {
  /**
   * 获取剧情库列表（默认仅已发布）
   */
  getStoryLibrary: (params = {}) => {
    const { status = 'published', page = 1, page_size = 20 } = params;
    return retryRequest(() => api.get('/story/library', {
      params: { status, page, page_size },
    }));
  },


  /**
   * 获取单个剧情详情
   */
  getStoryLibraryItem: (id) => {
    return retryRequest(() => api.get(`/story/library/${id}`));
  },

  /**
   * 更新故事状态
   * @param {number} id - 故事ID
   * @param {string} status - 新状态 (draft 或 published)
   */
  updateStoryStatus: (id, status) => {
    return retryRequest(() => api.patch(`/story/library/${id}/status`, null, {
      params: { status }
    }));
  },

  /**
   * 创建剧情（可选生成首段视频）
   * 默认 generate_initial_video=true 且 sync=false（异步生成）
   * 支持 FormData 方式提交（包含文件上传）
   */
  createStoryLibraryItem: (data, options = {}) => {
    const { generate_initial_video = true, sync = false } = options;
    
    // api.js 的拦截器会自动处理 FormData 的 Content-Type
    // 这里不需要额外配置
    
    return api.post('/story/library', data, {
      params: { generate_initial_video, sync },
      timeout: sync ? 600000 : 30000,
    });
  },
  /**
   * 获取预设故事场景列表
   * @returns {Promise} - 返回预设场景列表的Promise对象
   * 功能：从后端获取系统提供的预设故事场景（侦探、爱情、科幻等）
   */
  getScenarios: () => retryRequest(() => api.get('/story/scenarios')),

  /**
   * 初始化故事会话并生成第一个视频
   * @param {string} story - 故事背景描述
   * @param {string} initialScene - 初始场景描述
   * @param {string} customTitle - 自定义标题（可选）
   * @returns {Promise} - 返回包含session_id、视频URL、分支选项的Promise对象
   * 
   * 功能：
   * 1. 创建新的故事会话
   * 2. 生成第一轮视频（可能需要30-120秒）
   * 3. 返回视频URL和分支选项
   * 
   * ⚠️ 注意：此接口可能耗时较长，建议设置更长的超时时间
   */
  initStory: (story, initialScene, customTitle = null) => {
    console.log('📡 storyAPI.initStory - 开始初始化故事会话');
    console.log('📡 storyAPI.initStory - 故事:', story.substring(0, 50) + '...');
    console.log('📡 storyAPI.initStory - 初始场景:', initialScene.substring(0, 50) + '...');
    
    const requestData = {
      story,
      initial_scene: initialScene,
    };
    
    if (customTitle) {
      requestData.custom_title = customTitle;
    }
    
    // 视频生成需要较长时间，设置600秒超时
    return api.post('/story/init', requestData, {
      timeout: 600000, // 10分钟超时
    });
  },

  /**
   * 继续故事（用户选择分支后生成下一轮视频）
   * @param {number} libraryId - StoryLibrary 的 ID（可能是 draft 或 published）
   * @param {string} branchId - 用户选择的分支ID
   * @returns {Promise} - 返回包含新视频URL、新的分支选项和 library_id 的Promise对象
   * 
   * 功能：
   * 1. 根据 story status 判断处理方式：
   *    - draft: 更新现有 StoryLibrary
   *    - published: 创建新的 StoryLibrary（新交互）
   * 2. 生成下一轮视频（可能需要30-120秒）
   * 3. 返回新视频、新的分支选项和 library_id（如果是新创建的故事，会返回新的 library_id）
   * 
   * ⚠️ 注意：此接口可能耗时较长，建议设置更长的超时时间
   */
  continueStory: (libraryId, branchId) => {
    console.log('📡 storyAPI.continueStory - 继续故事');
    console.log('📡 storyAPI.continueStory - Library ID:', libraryId);
    console.log('📡 storyAPI.continueStory - 选择分支:', branchId);
    
    const requestData = {
      library_id: libraryId,
      selected_branch_id: branchId,
    };
    
    // 视频生成需要较长时间，设置600秒超时
    return api.post('/story/continue', requestData, {
      timeout: 600000, // 10分钟超时
    });
  },

  /**
   * 删除分支
   * @param {number} libraryId - StoryLibrary 的 ID
   * @param {string} branchId - 要删除的分支ID
   * @returns {Promise} - 返回删除后的分支列表
   */
  deleteBranch: (libraryId, branchId) => {
    console.log('📡 storyAPI.deleteBranch - 删除分支');
    console.log('📡 storyAPI.deleteBranch - Library ID:', libraryId);
    console.log('📡 storyAPI.deleteBranch - 分支ID:', branchId);
    
    return retryRequest(() => api.delete(`/story/library/${libraryId}/branch/${branchId}`));
  },

};

// 导出默认的storyAPI对象（可选，保持与其他API服务的一致性）
export default storyAPI;
